import { Checkbox, Input, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';

import { CUSTOM_LENS_TOOLTIP_PROPS } from 'components/molecules/PPA/Modals/CustomLense/customLens.ui';
import {
    DEFAULT_LENS_DIMENSION_SELECTION_COUNT,
    filterCatalogItems,
    getCatalogSectionItems,
    getCatalogSelectionSummary,
    getDimensionSelectionLimitState,
    getDimensionsSection,
    getKpisSection,
    getSystemColumnItems,
    LENS_CATALOG_SECTION_KEYS,
    LENS_DIMENSION_SELECTION_LIMIT_TOOLTIP,
    isLensCatalogApplyEnabled,
    isLensCatalogResetEnabled,
    setKpiSectionSelection,
    shouldBlockDimensionSelection,
    toggleCatalogItemSelection,
} from './scenarioLensCatalog.helpers';
import type { LensCatalogData, LensCatalogItem } from './scenarioLensCatalog.types';
import {
    StyledLensConfigColumn,
    StyledLensConfigColumnHeader,
    StyledLensConfigColumnList,
    StyledLensConfigItem,
    StyledLensConfigPanelBody,
    StyledLensConfigPanelHeader,
    StyledLensConfigEditButton,
    StyledLensConfigSearch,
    StyledLensConfigColumns,
    StyledLensConfigSystemRow,
    StyledLensConfigFooter,
    StyledLensFooterApplyButton,
    StyledLensFooterResetButton,
} from './ScenarioLensSection.styles';

type LensConfigurationPanelProps = {
    catalog: LensCatalogData;
    baselineCatalog?: LensCatalogData | null;
    onChange: (catalog: LensCatalogData) => void;
    onEdit?: () => void;
    onApply?: () => void;
    onReset?: () => void;
    readOnly?: boolean;
};

type ConfigColumnProps = {
    title: string;
    items: LensCatalogItem[];
    searchTerm: string;
    uiRole: 'DIMENSION' | 'KPI';
    onToggle: (catalogId: number, isSelected: boolean) => void;
    onToggleAll?: (isSelected: boolean) => void;
    showSelectAll?: boolean;
    readOnly?: boolean;
};

const ConfigColumn = ({
    title,
    items,
    searchTerm,
    uiRole,
    onToggle,
    onToggleAll,
    showSelectAll = false,
    readOnly = false,
}: ConfigColumnProps) => {
    const selectableItems = useMemo(() => items.filter((item) => !item.is_disabled), [items]);
    const visibleItems = useMemo(
        () => filterCatalogItems(selectableItems, searchTerm),
        [selectableItems, searchTerm],
    );
    const isDimensionColumn = uiRole === 'DIMENSION';
    const dimensionLimitState = useMemo(
        () => (isDimensionColumn ? getDimensionSelectionLimitState(selectableItems) : null),
        [isDimensionColumn, selectableItems],
    );
    const { label: selectionLabel } = getCatalogSelectionSummary(
        selectableItems,
        isDimensionColumn ? DEFAULT_LENS_DIMENSION_SELECTION_COUNT : undefined,
    );
    const allSelectableSelected =
        selectableItems.length > 0 && selectableItems.every((item) => item.is_selected);
    const someSelectableSelected = selectableItems.some((item) => item.is_selected);

    if (selectableItems.length === 0) {
        return null;
    }

    return (
        <StyledLensConfigColumn>
            <StyledLensConfigColumnHeader uiRole={uiRole}>
                {title} ({selectionLabel})
            </StyledLensConfigColumnHeader>
            <StyledLensConfigColumnList uiRole={uiRole}>
                {showSelectAll && onToggleAll ? (
                    <StyledLensConfigItem isSelected={someSelectableSelected}>
                        <Checkbox
                            checked={allSelectableSelected}
                            indeterminate={someSelectableSelected && !allSelectableSelected}
                            disabled={readOnly || selectableItems.length === 0}
                            onChange={(event) => onToggleAll(event.target.checked)}
                            className="lens-config-item-checkbox !m-0"
                        >
                            (All)
                        </Checkbox>
                    </StyledLensConfigItem>
                ) : null}
                {visibleItems.map((item) => {
                    const isDimensionLimitBlocked =
                        dimensionLimitState != null &&
                        shouldBlockDimensionSelection(item, dimensionLimitState.isLimitReached);
                    const isItemDisabled = readOnly || item.is_disabled || isDimensionLimitBlocked;
                    const tooltipTitle = item.is_disabled
                        ? item.disabled_reason ?? 'Unavailable'
                        : isDimensionLimitBlocked
                          ? LENS_DIMENSION_SELECTION_LIMIT_TOOLTIP
                          : undefined;

                    return (
                        <Tooltip
                            key={item.catalog_id}
                            {...(tooltipTitle ? CUSTOM_LENS_TOOLTIP_PROPS : {})}
                            title={tooltipTitle}
                        >
                            <StyledLensConfigItem
                                isDisabled={isItemDisabled}
                                isSelected={item.is_selected}
                            >
                                <Checkbox
                                    checked={item.is_selected}
                                    disabled={isItemDisabled}
                                    onChange={(event) => onToggle(item.catalog_id, event.target.checked)}
                                    className="lens-config-item-checkbox !m-0"
                                >
                                    {item.display_name}
                                </Checkbox>
                            </StyledLensConfigItem>
                        </Tooltip>
                    );
                })}
            </StyledLensConfigColumnList>
        </StyledLensConfigColumn>
    );
};

const LensConfigurationPanel = ({
    catalog,
    baselineCatalog = null,
    onChange,
    onEdit,
    onApply,
    onReset,
    readOnly = false,
}: LensConfigurationPanelProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const canApply = useMemo(
        () => isLensCatalogApplyEnabled(catalog, baselineCatalog),
        [catalog, baselineCatalog],
    );
    const canReset = useMemo(
        () => isLensCatalogResetEnabled(catalog, baselineCatalog),
        [catalog, baselineCatalog],
    );

    const systemColumnItems = getSystemColumnItems(catalog);
    const dimensionsSection = getDimensionsSection(catalog);
    const kpisSection = getKpisSection(catalog);
    const dimensionItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS);
    const kpiItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.KPIS);
    const dimensionsTitle = dimensionsSection?.section_name ?? 'Dimensions';
    const kpisTitle = kpisSection?.section_name ?? 'User Inputs / KPIs';

    const handleToggle = (catalogId: number, isSelected: boolean) => {
        onChange(toggleCatalogItemSelection(catalog, catalogId, isSelected));
    };

    const handleToggleAllKpis = (isSelected: boolean) => {
        onChange(setKpiSectionSelection(catalog, isSelected));
    };

    return (
        <StyledLensConfigPanelBody>
            <StyledLensConfigPanelHeader>
                {!readOnly && onEdit ? (
                    <StyledLensConfigEditButton type="button" onClick={onEdit}>
                        Edit
                    </StyledLensConfigEditButton>
                ) : null}
                <StyledLensConfigSearch>
                    <Input
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search"
                        suffix={<SearchOutlined className="text-black/45" />}
                        className="h-8 !rounded-md"
                    />
                </StyledLensConfigSearch>
                <span
                    className="pointer-events-none absolute bottom-0 left-2 right-2  border-b border-solid border-[rgba(0,0,0,0.06)]"
                    aria-hidden
                />
            </StyledLensConfigPanelHeader>
            {systemColumnItems.length > 0 ? (
                <StyledLensConfigSystemRow>
                    {systemColumnItems.map((item) => (
                        <Tooltip
                            key={item.catalog_id}
                            title={item.is_disabled ? item.disabled_reason ?? 'Unavailable' : undefined}
                        >
                            <Checkbox
                                checked={item.is_selected}
                                disabled={readOnly || item.is_disabled}
                                onChange={(event) => handleToggle(item.catalog_id, event.target.checked)}
                            >
                                {item.display_name}
                            </Checkbox>
                        </Tooltip>
                    ))}
                </StyledLensConfigSystemRow>
            ) : null}
            <StyledLensConfigColumns>
                {dimensionsSection ? (
                    <ConfigColumn
                        title={dimensionsTitle}
                        items={dimensionItems}
                        searchTerm={searchTerm}
                        uiRole="DIMENSION"
                        onToggle={handleToggle}
                        readOnly={readOnly}
                    />
                ) : null}
                {kpisSection ? (
                    <ConfigColumn
                        title={kpisTitle}
                        items={kpiItems}
                        searchTerm={searchTerm}
                        uiRole="KPI"
                        onToggle={handleToggle}
                        onToggleAll={handleToggleAllKpis}
                        showSelectAll
                        readOnly={readOnly}
                    />
                ) : null}
            </StyledLensConfigColumns>
            {!readOnly && onApply && onReset ? (
                <StyledLensConfigFooter>
                    <StyledLensFooterResetButton disabled={!canReset} onClick={onReset}>
                        Reset
                    </StyledLensFooterResetButton>
                    <StyledLensFooterApplyButton type="primary" disabled={!canApply} onClick={onApply}>
                        Apply
                    </StyledLensFooterApplyButton>
                </StyledLensConfigFooter>
            ) : null}
        </StyledLensConfigPanelBody>
    );
};

export default LensConfigurationPanel;
